import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #renderGeneration = 0;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
  const renderToken = ++this.#renderGeneration;
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        if (renderToken !== this.#renderGeneration) return;
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;
    const renderToken = ++this.#renderGeneration;
    event.promise
      ?.then(({ detail }) => {
        if (renderToken !== this.#renderGeneration) return;
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    const renderToken = ++this.#renderGeneration;

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        if (renderToken === this.#renderGeneration) {
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    const renderToken = ++this.#renderGeneration;

    event.promise
      ?.then(async ({ detail }) => {
      if (renderToken !== this.#renderGeneration) return;
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          await morphSection(this.sectionId, cartItemsHtml, morphOptions);


      // SAFA fix: on some mobile browsers a line item can come back from
      // the drawer morph with its title/image left blank even though its
      // price, quantity, and discount data rendered correctly (a stale/
      // mismatched node from the underlying DOM diff). Verify each row and,
      // if one is incomplete, repair it directly from the section HTML we
      // just received from the server - the same markup morphSection used -
      // instead of relying on the diff for that row.
      this.#repairIncompleteCartRows(cartItemsHtml);

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();

        // SAFA fix: the reservation-bar's inline <script> in cart-upsell-panel.liquid
        // only executes on a real page load, not when it's inserted via this section
        // morph — so after adding the first item to a previously empty cart, that
        // script never runs and its countdown never starts. This keeps it live
        // without touching that snippet.
        this.#ensureSafaReserveTimer();
        this.#ensureSafaUpsellBinding();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };
  /**
   * SAFA fix: on some mobile browsers, morphing the cart-drawer markup can
   * leave the item list out of sync with what the server actually sent -
   * either a whole row missing (drawer shows totals/discount but no
   * product line at all) or a row present with its title/image left blank
   * while price, quantity, and discount data render correctly. Not
   * reproducible on desktop, but confirmed from a user screen recording.
   * This compares the rendered rows against the section HTML the server
   * just sent (the same markup morphSection already used) and, if the set
   * of rows doesn't match or any row is incomplete, replaces the whole
   * row list with the fresh markup directly - instead of relying on the
   * diff, which is what left things out of sync in the first place.
   */
  #repairIncompleteCartRows(freshHtml) {
    if (!freshHtml) return;
    const liveTbody = this.querySelector('tbody[role="rowgroup"]');
    if (!liveTbody) return;

    const freshDocument = new DOMParser().parseFromString(freshHtml, 'text/html');
    const freshTbody = freshDocument.querySelector('tbody[role="rowgroup"]');
    if (!freshTbody) return;

    const liveRows = Array.from(liveTbody.querySelectorAll(':scope > tr[data-key]'));
    const freshRows = Array.from(freshTbody.querySelectorAll(':scope > tr[data-key]'));
    if (!freshRows.length) return;

    const liveKeys = liveRows.map((row) => row.dataset.key);
    const freshKeys = freshRows.map((row) => row.dataset.key);
    const sameRows = liveKeys.length === freshKeys.length && liveKeys.every((key, i) => key === freshKeys[i]);

    let needsRepair = !sameRows;
    if (!needsRepair) {
      for (const row of liveRows) {
        const titleLink = row.querySelector('.cart-items__title-row a, .cart-items__product-info a');
        const image = row.querySelector('.cart-items__media img');
        const titleText = titleLink?.textContent.trim() ?? '';
        const imageSrc = image?.getAttribute('src') ?? '';
        if (!titleText || !imageSrc) {
          needsRepair = true;
          break;
        }
      }
    }

    if (needsRepair) liveTbody.innerHTML = freshTbody.innerHTML;
  }


  /**
   * Fallback initializer for the SAFA cart-reservation countdown bar
   * (see snippets/cart-upsell-panel.liquid).
   *
   * That snippet's own inline <script> is only present in the server-
   * rendered HTML when the cart already has items, and it only executes
   * when the browser does a real page parse - not when the markup is
   * inserted later via this section morph. So when the bar first appears
   * through a morph (e.g. adding the first item to a previously empty
   * cart), that inline script never runs and its countdown never starts,
   * leaving the bar frozen until the page is reloaded. This mirrors that
   * script's own timer logic so the countdown stays live without ever
   * modifying cart-upsell-panel.liquid.
   */
  #ensureSafaReserveTimer() {
    const bar = document.getElementById('safaReserveBar');
    if (!bar || bar.dataset.safaInitialized === 'true' || bar.dataset.safaFallbackHooked === 'true') return;
    bar.dataset.safaFallbackHooked = 'true';

    const timer = bar.querySelector('[data-safa-timer]');
    const label = bar.querySelector('[data-safa-label]');
    const close = bar.querySelector('[data-safa-close]');
    const storageKey = 'safa-cart-reservation';
    const duration = 10 * 60 * 1000;
    let startedAt = Number(localStorage.getItem(storageKey));
    if (!startedAt || isNaN(startedAt) || Date.now() - startedAt >= duration) {
      startedAt = Date.now();
      localStorage.setItem(storageKey, String(startedAt));
    }

    const renderTimer = () => {
      const remaining = Math.max(0, duration - (Date.now() - startedAt));
      const seconds = Math.ceil(remaining / 1000);
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      if (timer) timer.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      bar.classList.toggle('low', seconds <= 120);
      if (seconds <= 0 && label) label.textContent = 'Reservation expired';
    };
    renderTimer();
    const interval = setInterval(renderTimer, 1000);
    if (close) {
      close.addEventListener('click', () => {
        bar.style.display = 'none';
        clearInterval(interval);
      });
    }
  }
  
  
  /**
   * SAFA fix: the upsell panel's inline <script> in cart-upsell-panel.liquid
   * binds the "ADD SUNGLASSES" / "ADD BERMUDA SHORTS" buttons via a click
   * delegation guarded by window.__safaUpsellBound, but - like the
   * reservation timer above - that inline script only executes on a real
   * page parse, never when its markup is inserted later via this section
   * morph. So after adding the first item to a previously empty cart, those
   * buttons render but silently do nothing when clicked.
   *
   * This binds the same delegation as a fallback, but instead of doing its
   * own ad-hoc DOM replace (which left the header cart bubble stale - the
   * count only updated after a reload), it dispatches a real
   * CartLinesUpdateEvent, the exact same event the theme's own add-to-cart
   * flow dispatches. That means every other component that already listens
   * for it - the header cart-icon bubble AND this cart-items-component's
   * own #handleCartUpdate (which includes the row-repair fix above) -
   * updates itself immediately, the normal way, without ever touching
   * cart-upsell-panel.liquid.
   */
  #ensureSafaUpsellBinding() {
    if (window.__safaUpsellBound) return;
    window.__safaUpsellBound = true;
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-safa-add]');
      if (!button) return;
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'ADDING…';

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const addResponse = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        });
        const addResult = await addResponse.json();
        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.message || 'Add to cart failed');
        }
        const ajaxCart = await (await fetch(root + 'cart.js')).json();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'safa-upsell-panel',
            sourceId: 'safa-upsell',
            itemCount: ajaxCart.item_count,
            sections: addResult.sections,
            didError: false,
          },
        });
        button.disabled = false;
        button.textContent = originalText;
      } catch (e) {
        console.error('SAFA cart refresh failed:', e);
        deferredEventPromise.resolve({ cart: undefined, detail: { didError: true } });
        button.disabled = false;
        button.textContent = originalText;
      }
    });
  }





  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}